import { useEffect, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Carregando, Aviso } from "../../components/Layout";
import { api, fmt, type ErroApi } from "../../lib/api";
import { PODE_HOMOLOGAR, type Sessao } from "./Admin";

type Recurso = {
  id: number; protocolo: string; tipo: string; anexo: string; status: string;
  criadoEm: string; candidato: string; token: string; cargo: string;
  justificativa: string; documentoId: number | null;
  decisao: string | null; julgadoPor: string | null;
};

/**
 * Julgamento dos recursos.
 *
 * A fundamentação é obrigatória porque é ela que vai para o candidato, e porque
 * a decisão da Comissão é última instância (itens 11.3 e 15.18): não há etapa
 * seguinte em que o motivo possa ser esclarecido.
 */
export function Recursos({ sessao }: { sessao: Sessao }) {
  const [lista, setLista] = useState<Recurso[] | null>(null);
  const [decisoes, setDecisoes] = useState<Record<number, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const podeJulgar = PODE_HOMOLOGAR.includes(sessao.perfil ?? "");

  async function carregar() {
    try { setLista(await api.get<Recurso[]>("/api/admin/recursos")); }
    catch { setLista([]); }
  }

  useEffect(() => { void carregar(); }, []);

  async function julgar(id: number, deferir: boolean) {
    const decisao = (decisoes[id] ?? "").trim();
    if (decisao.length < 10) {
      setErro("Escreva a fundamentação da decisão: ela é comunicada ao candidato.");
      return;
    }
    setOcupado(true); setErro(null);
    try {
      await api.post(`/api/admin/recursos/${id}/julgar`, { deferir, decisao });
      await carregar();
    } catch (e) { setErro((e as ErroApi).mensagem); }
    finally { setOcupado(false); }
  }

  if (!lista) return <Carregando texto="Carregando recursos" />;

  const pendentes = lista.filter((r) => r.status === "RECEBIDO");
  const julgados = lista.filter((r) => r.status !== "RECEBIDO");

  return (
    <div className="pilha pilha-6">
      <div>
        <h1>Recursos</h1>
        <p className="texto-secundario">
          {pendentes.length} pendente(s) · {julgados.length} julgado(s)
        </p>
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {lista.length === 0 && <p className="vazio">Nenhum recurso protocolado.</p>}

      {pendentes.map((r) => (
        <article key={r.id} className="painel">
          <div className="painel-cabecalho linha linha-entre linha-quebra">
            <div>
              <p className="rotulo-sobre">Protocolo {r.protocolo} · Anexo {r.anexo}</p>
              <strong>{r.candidato}</strong>
              <span className="texto-sutil"> · {r.cargo} · </span>
              <span className="mono texto-sutil">{r.token}</span>
            </div>
            <span className="situacao situacao-EM_ANALISE">aguardando julgamento</span>
          </div>

          <div className="painel-corpo pilha">
            <div>
              <p className="rotulo-sobre">Razão apresentada pelo candidato</p>
              <p style={{ whiteSpace: "pre-wrap" }}>{r.justificativa}</p>
              <p className="texto-sutil num">Protocolado em {fmt.dataHora(r.criadoEm)}</p>
            </div>

            {r.documentoId && (
              <a className="btn btn-secundario btn-pequeno" style={{ alignSelf: "flex-start" }}
                 href={`/api/admin/inscricoes/documentos/${r.documentoId}`}
                 target="_blank" rel="noreferrer">
                <Icon nome="arquivo" tamanho={16} /> Abrir anexo em PDF
              </a>
            )}

            {podeJulgar ? (
              <>
                <div className="campo">
                  <label className="campo-rotulo" htmlFor={`dec-${r.id}`}>
                    Fundamentação da decisão
                  </label>
                  <span className="campo-ajuda">
                    Este texto é exibido ao candidato. A decisão é última instância.
                  </span>
                  <textarea id={`dec-${r.id}`} className="area-texto" style={{ minHeight: 110 }}
                            value={decisoes[r.id] ?? ""}
                            onChange={(e) => setDecisoes({ ...decisoes, [r.id]: e.target.value })} />
                </div>
                <div className="linha linha-quebra">
                  <button className="btn btn-primario" disabled={ocupado}
                          onClick={() => julgar(r.id, true)}>
                    <Icon nome="checado" tamanho={18} /> Deferir recurso
                  </button>
                  <button className="btn btn-perigo" disabled={ocupado}
                          onClick={() => julgar(r.id, false)}>
                    <Icon nome="cancelar" tamanho={18} /> Indeferir recurso
                  </button>
                </div>
                <p className="texto-sutil">
                  Deferir um recurso de inscrição indeferida reverte o indeferimento
                  automaticamente e registra a reversão na auditoria.
                </p>
              </>
            ) : (
              <Aviso tipo="info">
                O julgamento de recursos é atribuição do Presidente da Comissão.
              </Aviso>
            )}
          </div>
        </article>
      ))}

      {julgados.length > 0 && (
        <div className="painel">
          <div className="painel-cabecalho"><h2 className="h3">Recursos julgados</h2></div>
          <div className="tabela-rolagem">
            <table className="tabela">
              <caption className="so-leitor">Recursos já julgados pela comissão</caption>
              <thead>
                <tr>
                  <th scope="col">Protocolo</th><th scope="col">Candidato</th>
                  <th scope="col">Decisão</th><th scope="col">Fundamentação</th>
                  <th scope="col">Julgado por</th>
                </tr>
              </thead>
              <tbody>
                {julgados.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.protocolo}</td>
                    <td>{r.candidato}</td>
                    <td>
                      <span className={`situacao situacao-${r.status === "DEFERIDO" ? "DEFERIDA" : "INDEFERIDA"}`}>
                        {r.status.toLowerCase()}
                      </span>
                    </td>
                    <td>{r.decisao}</td>
                    <td className="texto-sutil">{r.julgadoPor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
